import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../AuthContext";
import {
    getTecDocMonitoring,
    searchStructuredLogs,
    getLogTimeline,
    getEndpointAggregations,
    getCorrelationAggregations,
    getLogAlerts,
} from "../../api";
import LogFilters from "../components/LogFilters";
import LogTable from "../components/LogTable";
import LogTimeline from "../components/LogTimeline";
import LogAggregations from "../components/LogAggregations";
import LogAlertsPanel from "../components/LogAlertsPanel";

const REFRESH_INTERVAL_MS = 15000;
const LOG_PAGE_SIZE = 25;

const severityStyles = {
    warning: "border-amber-200 bg-amber-50 text-amber-700",
    critical: "border-red-200 bg-red-50 text-red-700",
};

const breakerStyles = {
    OPEN: "bg-red-100 text-red-700",
    HALF_OPEN: "bg-amber-100 text-amber-700",
    CLOSED: "bg-emerald-100 text-emerald-700",
};

const stateLabels = {
    TRIGGERED: { label: "Ausgelöst", className: "text-red-600" },
    RESOLVED: { label: "Entwarnt", className: "text-emerald-600" },
};

function createEmptyFilters() {
    return {
        correlationId: "",
        event: "",
        level: "",
        status: "",
        from: "",
        to: "",
    };
}

function toIsoDateTime(value) {
    if (!value) {
        return undefined;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return undefined;
    }
    return date.toISOString();
}

function formatPercent(value) {
    if (typeof value !== "number" || Number.isNaN(value)) {
        return "–";
    }
    return `${(value * 100).toFixed(1)} %`;
}

function formatNumber(value, fractionDigits = 0) {
    if (typeof value !== "number" || Number.isNaN(value)) {
        return "–";
    }
    return value.toLocaleString("de-DE", {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
}

function formatTimestamp(value) {
    if (!value) {
        return "–";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "–";
    }
    return date.toLocaleString("de-DE");
}

function MetricCard({ title, value, helper }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="mt-3 text-2xl font-semibold text-slate-900">{value}</p>
            {helper ? <p className="mt-2 text-xs text-slate-500">{helper}</p> : null}
        </div>
    );
}

function ActiveAlarmCard({ alarm }) {
    const severityClass = severityStyles[alarm.severity] || "border-slate-200 bg-slate-50 text-slate-700";
    return (
        <div className={`rounded-lg border px-4 py-3 shadow-sm ${severityClass}`}>
            <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">{alarm.type}</div>
                <div className="text-xs font-medium uppercase tracking-wide">{alarm.severity}</div>
            </div>
            <p className="mt-2 text-sm">{alarm.message}</p>
            <p className="mt-2 text-xs text-slate-600">seit {formatTimestamp(alarm.since)}</p>
        </div>
    );
}

function BreakerBadge({ state }) {
    const normalized = state ?? "";
    const badgeClass = breakerStyles[normalized] || "bg-slate-100 text-slate-700";
    const label = useMemo(() => {
        switch (normalized) {
            case "OPEN":
                return "Offen";
            case "HALF_OPEN":
                return "Halb offen";
            case "CLOSED":
                return "Geschlossen";
            default:
                return normalized || "Unbekannt";
        }
    }, [normalized]);
    return <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${badgeClass}`}>{label}</span>;
}

export default function DevOpsDashboardPage() {
    const { token } = useContext(AuthContext);
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const isMountedRef = useRef(false);
    const [logFilters, setLogFilters] = useState(() => createEmptyFilters());
    const [appliedFilters, setAppliedFilters] = useState(() => createEmptyFilters());
    const [logResults, setLogResults] = useState({ content: [], totalElements: 0, totalPages: 0, page: 0, size: LOG_PAGE_SIZE });
    const [logLoading, setLogLoading] = useState(false);
    const [logErrorState, setLogErrorState] = useState(null);
    const [logPage, setLogPage] = useState(0);
    const [logUpdatedAt, setLogUpdatedAt] = useState(null);
    const [selectedCorrelation, setSelectedCorrelation] = useState(null);
    const [timelineData, setTimelineData] = useState({ entries: [] });
    const [timelineLoading, setTimelineLoading] = useState(false);
    const [timelineError, setTimelineError] = useState(null);
    const [endpointAggregations, setEndpointAggregations] = useState([]);
    const [correlationAggregations, setCorrelationAggregations] = useState([]);
    const [aggregationsLoading, setAggregationsLoading] = useState(false);
    const [aggregationsError, setAggregationsError] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [alertsLoading, setAlertsLoading] = useState(false);
    const [alertsError, setAlertsError] = useState(null);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const refreshMetrics = useCallback(async () => {
        if (!token) {
            return null;
        }
        try {
            const data = await getTecDocMonitoring(token);
            if (isMountedRef.current) {
                setMetrics(data);
                setError(null);
            }
            return data;
        } catch (err) {
            if (isMountedRef.current) {
                setError(err?.message || "Monitoring konnte nicht geladen werden.");
            }
            throw err;
        }
    }, [token]);

    const buildSearchParams = useCallback((filters) => {
        const params = {};
        if (!filters) {
            return params;
        }
        if (filters.correlationId && filters.correlationId.trim()) {
            params.correlationId = filters.correlationId.trim();
        }
        if (filters.event && filters.event.trim()) {
            params.event = filters.event.trim();
        }
        if (filters.level) {
            params.level = filters.level;
        }
        if (filters.status) {
            const parsed = Number.parseInt(filters.status, 10);
            if (!Number.isNaN(parsed)) {
                params.status = parsed;
            }
        }
        const fromIso = toIsoDateTime(filters.from);
        if (fromIso) {
            params.from = fromIso;
        }
        const toIsoValue = toIsoDateTime(filters.to);
        if (toIsoValue) {
            params.to = toIsoValue;
        }
        return params;
    }, []);

    const buildRangeParams = useCallback((filters) => {
        const params = {};
        if (!filters) {
            return params;
        }
        const fromIso = toIsoDateTime(filters.from);
        if (fromIso) {
            params.from = fromIso;
        }
        const toIsoValue = toIsoDateTime(filters.to);
        if (toIsoValue) {
            params.to = toIsoValue;
        }
        return params;
    }, []);

    const fetchLogs = useCallback(async (pageNumber = 0, filtersOverride) => {
        if (!token) {
            return null;
        }
        setLogLoading(true);
        const filterSet = filtersOverride ?? appliedFilters;
        try {
            const response = await searchStructuredLogs(token, {
                ...buildSearchParams(filterSet),
                page: Math.max(0, pageNumber),
                size: LOG_PAGE_SIZE,
            });
            if (!isMountedRef.current) {
                return response;
            }
            setLogResults({
                content: response?.content ?? [],
                totalElements: response?.totalElements ?? 0,
                totalPages: response?.totalPages ?? 0,
                page: response?.page ?? Math.max(0, pageNumber),
                size: response?.size ?? LOG_PAGE_SIZE,
            });
            setLogErrorState(null);
            setLogUpdatedAt(new Date().toISOString());
            return response;
        } catch (err) {
            if (isMountedRef.current) {
                setLogErrorState(err?.message || "Logs konnten nicht geladen werden.");
                setLogResults((prev) => ({ ...prev, content: [], totalElements: 0, totalPages: 0 }));
            }
            throw err;
        } finally {
            if (isMountedRef.current) {
                setLogLoading(false);
            }
        }
    }, [token, appliedFilters, buildSearchParams]);

    const handleFilterChange = useCallback((nextFilters) => {
        setLogFilters(nextFilters);
    }, []);

    const handleResetFilters = useCallback(() => {
        const empty = createEmptyFilters();
        setLogFilters(empty);
        setAppliedFilters(empty);
        setLogPage(0);
        setSelectedCorrelation(null);
        setTimelineData({ entries: [] });
        setTimelineError(null);
    }, []);

    const handleSearchLogs = useCallback(() => {
        const sanitized = { ...logFilters };
        setAppliedFilters(sanitized);
        setLogPage(0);
    }, [logFilters]);

    const handlePageChange = useCallback((nextPage) => {
        setLogPage(nextPage);
    }, []);

    const handleSelectCorrelation = useCallback((correlationId) => {
        if (!correlationId) {
            setSelectedCorrelation(null);
            return;
        }
        setSelectedCorrelation((prev) => (prev === correlationId ? null : correlationId));
    }, []);

    useEffect(() => {
        if (!token) {
            setMetrics(null);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        refreshMetrics()
            .catch(() => null)
            .finally(() => {
                if (!cancelled && isMountedRef.current) {
                    setLoading(false);
                }
            });
        const intervalId = window.setInterval(() => {
            refreshMetrics().catch(() => null);
        }, REFRESH_INTERVAL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [token, refreshMetrics]);

    useEffect(() => {
        if (!token) {
            setLogFilters(createEmptyFilters());
            setAppliedFilters(createEmptyFilters());
            setLogResults({ content: [], totalElements: 0, totalPages: 0, page: 0, size: LOG_PAGE_SIZE });
            setLogLoading(false);
            setLogErrorState(null);
            setLogUpdatedAt(null);
            setLogPage(0);
            setSelectedCorrelation(null);
            setTimelineData({ entries: [] });
            setTimelineError(null);
            setEndpointAggregations([]);
            setCorrelationAggregations([]);
            setAggregationsLoading(false);
            setAggregationsError(null);
            setAlerts([]);
            setAlertsLoading(false);
            setAlertsError(null);
        }
    }, [token]);

    useEffect(() => {
        setLogPage(0);
        setSelectedCorrelation(null);
        setTimelineData({ entries: [] });
        setTimelineError(null);
    }, [appliedFilters]);

    useEffect(() => {
        if (!token) {
            return;
        }
        fetchLogs(logPage).catch(() => null);
    }, [token, appliedFilters, logPage, fetchLogs]);

    useEffect(() => {
        if (!token) {
            return;
        }
        setAggregationsLoading(true);
        setAggregationsError(null);
        const params = buildRangeParams(appliedFilters);
        Promise.all([
            getEndpointAggregations(token, params),
            getCorrelationAggregations(token, params),
        ])
            .then(([endpointData, correlationData]) => {
                if (!isMountedRef.current) {
                    return;
                }
                setEndpointAggregations(endpointData ?? []);
                setCorrelationAggregations(correlationData ?? []);
                setAggregationsError(null);
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                setAggregationsError(err?.message || "Aggregationen konnten nicht geladen werden.");
                setEndpointAggregations([]);
                setCorrelationAggregations([]);
            })
            .finally(() => {
                if (isMountedRef.current) {
                    setAggregationsLoading(false);
                }
            });
    }, [token, appliedFilters, buildRangeParams]);

    useEffect(() => {
        if (!token) {
            return;
        }
        setAlertsLoading(true);
        setAlertsError(null);
        const params = buildRangeParams(appliedFilters);
        getLogAlerts(token, params)
            .then((data) => {
                if (!isMountedRef.current) {
                    return;
                }
                setAlerts(data ?? []);
                setAlertsError(null);
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                setAlertsError(err?.message || "Alerts konnten nicht geladen werden.");
                setAlerts([]);
            })
            .finally(() => {
                if (isMountedRef.current) {
                    setAlertsLoading(false);
                }
            });
    }, [token, appliedFilters, buildRangeParams]);

    useEffect(() => {
        if (!token || !selectedCorrelation) {
            setTimelineLoading(false);
            if (!selectedCorrelation) {
                setTimelineData({ entries: [] });
                setTimelineError(null);
            }
            return;
        }
        setTimelineLoading(true);
        setTimelineError(null);
        getLogTimeline(token, selectedCorrelation)
            .then((data) => {
                if (!isMountedRef.current) {
                    return;
                }
                setTimelineData(data ?? { entries: [] });
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                setTimelineError(err?.message || "Timeline konnte nicht geladen werden.");
                setTimelineData({ entries: [] });
            })
            .finally(() => {
                if (isMountedRef.current) {
                    setTimelineLoading(false);
                }
            });
    }, [token, selectedCorrelation]);

    const activeAlarms = metrics?.activeAlarms ?? [];
    const alarmLog = useMemo(() => (metrics?.alarmLog ?? []).slice(0, 20), [metrics]);
    const logEntries = logResults?.content ?? [];
    const logTotalElements = logResults?.totalElements ?? 0;
    const logCurrentPage = logResults?.page ?? logPage;
    const logPageSizeValue = logResults?.size ?? LOG_PAGE_SIZE;
    const logsLastUpdated = logUpdatedAt ? formatTimestamp(logUpdatedAt) : "–";

    return (
        <div className="mx-auto max-w-6xl px-6 py-10">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-3xl font-semibold text-slate-900">DevOps Monitoring</h1>
                    <p className="mt-1 text-sm text-slate-600">
                        Echtzeitkennzahlen für Rate-Limits, Circuit-Breaker und Cache der TecDoc-Integration.
                    </p>
                </div>
                <div className="flex flex-col items-start gap-2 sm:items-end">
                    <button
                        type="button"
                        onClick={() => refreshMetrics().catch(() => null)}
                        className="inline-flex items-center gap-2 rounded-lg border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-600 hover:bg-blue-50"
                    >
                        ↻ Aktualisieren
                    </button>
                    <span className="text-xs text-slate-500">
                        Zuletzt aktualisiert: {metrics?.generatedAt ? formatTimestamp(metrics.generatedAt) : "–"}
                    </span>
                </div>
            </div>

            {error ? (
                <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {error}
                </div>
            ) : null}

            {loading ? (
                <div className="mt-10 rounded-lg border border-slate-200 bg-white p-6 text-center text-slate-500 shadow-sm">
                    Monitoring wird geladen ...
                </div>
            ) : (
                <>
                    <section className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                        <MetricCard
                            title="TecDoc Calls / Minute"
                            value={formatNumber(metrics?.callsPerMinute ?? 0)}
                            helper="Anzahl aller externen Requests in den letzten 60 Sekunden"
                        />
                        <MetricCard
                            title="4xx-Quote"
                            value={formatPercent(metrics?.fourXxRatio ?? 0)}
                            helper="Verhältnis von Client-Fehlern zu allen TecDoc-Calls"
                        />
                        <MetricCard
                            title="5xx-Quote"
                            value={formatPercent(metrics?.fiveXxRatio ?? 0)}
                            helper="Verhältnis von Server-Fehlern zu allen TecDoc-Calls"
                        />
                        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                            <p className="text-sm font-medium text-slate-500">Circuit-Breaker</p>
                            <div className="mt-3 flex items-center justify-between">
                                <BreakerBadge state={metrics?.breakerState} />
                                <span className="text-xs text-slate-500">Status tecdocCircuitBreaker</span>
                            </div>
                        </div>
                        <MetricCard
                            title="Cache-Hit-Rate"
                            value={formatPercent(metrics?.cacheHitRate ?? 0)}
                            helper="Trefferquote des Caffeine-Caches für TecDoc-Medien"
                        />
                    </section>

                    <section className="mt-10">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-slate-900">Aktive Alarme</h2>
                            <span className="text-sm text-slate-500">Schwellwert: ≥3 Ereignisse pro Minute</span>
                        </div>
                        {activeAlarms.length === 0 ? (
                            <p className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                                Keine aktiven Alarme. Alle Systeme arbeiten im Soll.
                            </p>
                        ) : (
                            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                                {activeAlarms.map((alarm) => (
                                    <ActiveAlarmCard key={`${alarm.type}-${alarm.since}`} alarm={alarm} />
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="mt-10">
                        <h2 className="text-xl font-semibold text-slate-900">Alarm-Historie</h2>
                        <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                            <table className="min-w-full divide-y divide-slate-200 text-sm">
                                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    <tr>
                                        <th className="px-4 py-3">Zeitpunkt</th>
                                        <th className="px-4 py-3">Typ</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3">Nachricht</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {alarmLog.length === 0 ? (
                                        <tr>
                                            <td className="px-4 py-4 text-center text-slate-500" colSpan={4}>
                                                Bisher wurden keine Alarme ausgelöst.
                                            </td>
                                        </tr>
                                    ) : (
                                        alarmLog.map((entry, index) => {
                                            const status = stateLabels[entry.state] ?? {
                                                label: entry.state,
                                                className: "text-slate-600",
                                            };
                                            return (
                                                <tr key={`${entry.timestamp}-${entry.type}-${index}`}>
                                                    <td className="px-4 py-3 text-slate-600">{formatTimestamp(entry.timestamp)}</td>
                                                    <td className="px-4 py-3 font-medium text-slate-800">{entry.type}</td>
                                                    <td className={`px-4 py-3 font-semibold ${status.className}`}>{status.label}</td>
                                                    <td className="px-4 py-3 text-slate-700">{entry.message}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section className="mt-16">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h2 className="text-xl font-semibold text-slate-900">Log-Dashboard</h2>
                                <p className="text-sm text-slate-600">
                                    Analyse korrelierter Requests, Events und Laufzeiten.
                                </p>
                            </div>
                            <div className="text-xs text-slate-500">Zuletzt aktualisiert: {logsLastUpdated}</div>
                        </div>
                        <div className="mt-5">
                            <LogFilters
                                filters={logFilters}
                                onChange={handleFilterChange}
                                onSearch={handleSearchLogs}
                                onReset={handleResetFilters}
                                isLoading={logLoading}
                            />
                        </div>
                        {logErrorState ? (
                            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                                {logErrorState}
                            </div>
                        ) : null}
                        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                            <div className="lg:col-span-2">
                                <LogTable
                                    logs={logEntries}
                                    page={logCurrentPage}
                                    size={logPageSizeValue}
                                    totalElements={logTotalElements}
                                    onPageChange={handlePageChange}
                                    onSelectCorrelation={handleSelectCorrelation}
                                    selectedCorrelationId={selectedCorrelation}
                                    isLoading={logLoading}
                                />
                            </div>
                            <div className="flex flex-col gap-3">
                                {timelineError ? (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700">
                                        {timelineError}
                                    </div>
                                ) : null}
                                <LogTimeline
                                    correlationId={selectedCorrelation}
                                    timeline={timelineData}
                                    isLoading={timelineLoading}
                                />
                            </div>
                        </div>
                        <div className="mt-8">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-slate-900">Aggregierte Kennzahlen</h3>
                                {aggregationsError ? (
                                    <span className="text-xs text-red-600">{aggregationsError}</span>
                                ) : null}
                            </div>
                            <div className="mt-4">
                                <LogAggregations
                                    endpointMetrics={endpointAggregations}
                                    correlationMetrics={correlationAggregations}
                                    isLoading={aggregationsLoading}
                                />
                            </div>
                        </div>
                        <div className="mt-8">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-slate-900">Alerts &amp; Benachrichtigungen</h3>
                                {alertsError ? (
                                    <span className="text-xs text-red-600">{alertsError}</span>
                                ) : null}
                            </div>
                            <div className="mt-4">
                                <LogAlertsPanel alerts={alerts} isLoading={alertsLoading} />
                            </div>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
